import {getMap, getLayerRegistry, PlanTrailActionContext} from "./types";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { MarkerGeometry } from "../../../../../utils/models/MarkerFeature";
import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { getElevationData } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { RouteFactory } from "../../../../../utils/models/Route";
import { i18n } from "../../../../../i18n";
import { ReverseRouteCommand, remapStageDestinations, reverseRouteInPlace } from "../../undo/commands/ReverseRouteCommand";
import { applyDayKeyedState, captureDayKeyedState, splitIntoDays } from "../../../services/split-into-days";
import { ReverseItineraryCommand, reverseItineraryStateInPlace } from "../../undo/commands/ReverseItineraryCommand";
import { LoopRouteCommand } from "../../undo/commands/LoopRouteCommand";
import { BacktrackRouteCommand } from "../../undo/commands/BacktrackRouteCommand";
import { SplitRouteCommand } from "../../undo/commands/SplitRouteCommand";
import { MergeRoutesCommand } from "../../undo/commands/MergeRoutesCommand";
import { MergeAllRoutesCommand } from "../../undo/commands/MergeAllRoutesCommand";
import { AddRouteCommand } from "../../undo/commands/AddRouteCommand";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { anchorToMarkers } from "../../../../../utils/models/mapMatch";
import { markerCoordinates } from "./map-match";
import { isWheeledSegment } from "../../../services/reverseRedraw";

// Translated when called (never at module load) so names and prompts follow the page's locale.
const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

const routeFingerprint = (route: { markers: any[]; lineStrings: any[] }): string =>
  JSON.stringify([route.markers, route.lineStrings]);

/** A reversed bike or car segment routed again in its new direction; on failure, the mirrored line. */
async function rerouted(mirrored: LineStringFeature, startMarker: any, endMarker: any): Promise<LineStringFeature> {
  const coords = mirrored.lineStringGeometry.coordinates;
  try {
    // Copies, because snapping writes the start marker's looked-up elevation back onto it.
    const line = await snapPoints(
      JSON.parse(JSON.stringify(startMarker)),
      JSON.parse(JSON.stringify(endMarker)),
      mirrored.lineStringProperties.profile!
    );
    return anchorToMarkers(
      line,
      markerCoordinates(startMarker) ?? coords[0],
      markerCoordinates(endMarker) ?? coords[coords.length - 1]
    );
  } catch (error) {
    console.error('Re-routing a reversed segment failed; keeping it without turns', error);
    return mirrored;
  }
}

/**
 * Merge two adjacent routes' markers + lineStrings without re-snapping existing segments.
 * If the routes share a join point (last marker of A == first marker of B), the duplicate
 * marker is dropped and existing lineStrings are concatenated. Otherwise a single connector
 * lineString is generated between the two routes.
 */
async function mergeRoutePair(
  keepMarkers: MarkerFeature[],
  keepLineStrings: LineStringFeature[],
  appendMarkers: MarkerFeature[],
  appendLineStrings: LineStringFeature[],
  dispatch: (action: string, payload?: any) => Promise<any>,
): Promise<{ markers: MarkerFeature[]; lineStrings: LineStringFeature[] }> {
  if (appendMarkers.length === 0) {
    return { markers: [...keepMarkers], lineStrings: [...keepLineStrings] };
  }
  if (keepMarkers.length === 0) {
    return { markers: [...appendMarkers], lineStrings: [...appendLineStrings] };
  }

  const lastCoords = keepMarkers[keepMarkers.length - 1].markerGeometry.coordinates || [undefined, undefined];
  const firstCoords = appendMarkers[0].markerGeometry.coordinates || [undefined, undefined];
  const samePoint = Array.isArray(lastCoords) && Array.isArray(firstCoords) &&
    lastCoords[0] === firstCoords[0] && lastCoords[1] === firstCoords[1];

  if (samePoint) {
    return {
      markers: [...keepMarkers, ...appendMarkers.slice(1)],
      lineStrings: [...keepLineStrings, ...appendLineStrings],
    };
  }

  // Routes don't share an endpoint — generate a single connector segment between them.
  const connector = await dispatch('createPolylineGeoJson', {
    startMarker: keepMarkers[keepMarkers.length - 1],
    endMarker: appendMarkers[0],
  });
  return {
    markers: [...keepMarkers, ...appendMarkers],
    lineStrings: [...keepLineStrings, connector, ...appendLineStrings],
  };
}

export const routeOperationsActions = {
  /**
   * Create a new route (the "Add Route" button, or "Add route to this day"
   * when `payload.day` is given). Wraps the bare `createNewRoute`/`updateRouteDay`
   * mutations in an AddRouteCommand so Undo can revert it — createNewRoute can
   * also silently reassign an existing route's day, so the whole routes map is
   * snapshotted before/after rather than just the new route.
   */
  async addNewRoute({ state, commit, dispatch }: PlanTrailActionContext, payload?: { name?: string, day?: number }): Promise<string> {
    const routesBefore = JSON.parse(JSON.stringify(state.routes));
    const activeRouteKeyBefore = state.activeRouteKey;

    commit('createNewRoute', payload?.name);
    const newRouteKey = state.activeRouteKey as string;

    if (payload?.day !== undefined) {
      commit('updateRouteDay', { routeId: newRouteKey, day: payload.day });
    }

    state.undoManager.push(new AddRouteCommand({
      newRouteKey,
      routesBefore,
      routesAfter: state.routes,
      activeRouteKeyBefore,
      activeRouteKeyAfter: state.activeRouteKey,
    }));

    await dispatch('addAllRoutesToMap');
    return newRouteKey;
  },

  async mergeRouteWithPrevious({ state, getters, commit, dispatch }: PlanTrailActionContext, routeId: string) {
    const ordered = getters['routesArray'] as any[];
    const idx = ordered.findIndex(r => r.id === routeId);
    if (idx <= 0) return;

    const prevRouteId = ordered[idx - 1].id as string;
    const currentRouteId = ordered[idx].id as string;

    const keep = state.routes[prevRouteId];
    const append = state.routes[currentRouteId];
    if (!keep || !append) return;

    // Capture state before merge
    const keepMarkersBefore = JSON.parse(JSON.stringify(keep.markers));
    const keepLineStringsBefore = JSON.parse(JSON.stringify(keep.lineStrings));
    const removedSnapshot = JSON.parse(JSON.stringify(append));
    delete removedSnapshot.undoMarkers; delete removedSnapshot.undoLineStrings;
    delete removedSnapshot.redoMarkers; delete removedSnapshot.redoLineStrings;
    const positionsBefore = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));
    const activeKeyBefore = state.activeRouteKey!;

    // Merge markers and lineStrings, preserving existing geometry instead of re-snapping
    const { markers: newMarkers, lineStrings: newLineStrings } = await mergeRoutePair(
      keep.markers, keep.lineStrings, append.markers, append.lineStrings, dispatch
    );

    keep.markers = newMarkers;
    keep.lineStrings = newLineStrings;
    keep.updated_at = new Date();

    commit('removeRoute', currentRouteId);
    const remainingOrder = (getters['routesArray'] as any[]).map(r => r.id);
    commit('updateRoutePositions', remainingOrder);
    commit('setActiveRoute', prevRouteId);

    // Sync legacy state since active route is now the merged one
    state.markers = [...keep.markers];
    state.lineStrings = [...keep.lineStrings];

    const positionsAfter = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));

    state.undoManager.push(new MergeRoutesCommand({
      keepRouteKey: prevRouteId,
      removedRouteKey: currentRouteId,
      keepRouteMarkersBefore: keepMarkersBefore,
      keepRouteLineStringsBefore: keepLineStringsBefore,
      keepRouteMarkersAfter: JSON.parse(JSON.stringify(keep.markers)),
      keepRouteLineStringsAfter: JSON.parse(JSON.stringify(keep.lineStrings)),
      removedRouteSnapshot: removedSnapshot,
      routePositionsBefore: positionsBefore,
      routePositionsAfter: positionsAfter,
      activeRouteKeyBefore: activeKeyBefore,
    }));

    await dispatch('addAllRoutesToMap');
  },

  async mergeRouteWithNext({ state, getters, commit, dispatch }: PlanTrailActionContext, routeId: string) {
    const ordered = getters['routesArray'] as any[];
    const idx = ordered.findIndex(r => r.id === routeId);
    if (idx === -1 || idx >= ordered.length - 1) return;

    const currentRouteId = ordered[idx].id as string;
    const nextRouteId = ordered[idx + 1].id as string;

    const keep = state.routes[currentRouteId];
    const append = state.routes[nextRouteId];
    if (!keep || !append) return;

    // Capture state before merge
    const keepMarkersBefore = JSON.parse(JSON.stringify(keep.markers));
    const keepLineStringsBefore = JSON.parse(JSON.stringify(keep.lineStrings));
    const removedSnapshot = JSON.parse(JSON.stringify(append));
    delete removedSnapshot.undoMarkers; delete removedSnapshot.undoLineStrings;
    delete removedSnapshot.redoMarkers; delete removedSnapshot.redoLineStrings;
    const positionsBefore = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));
    const activeKeyBefore = state.activeRouteKey!;

    // Merge markers and lineStrings, preserving existing geometry instead of re-snapping
    const { markers: newMarkers, lineStrings: newLineStrings } = await mergeRoutePair(
      keep.markers, keep.lineStrings, append.markers, append.lineStrings, dispatch
    );

    keep.markers = newMarkers;
    keep.lineStrings = newLineStrings;
    keep.updated_at = new Date();

    commit('removeRoute', nextRouteId);
    const remainingOrder = (getters['routesArray'] as any[]).map(r => r.id);
    commit('updateRoutePositions', remainingOrder);
    commit('setActiveRoute', currentRouteId);

    // Sync legacy state since active route is now the merged one
    state.markers = [...keep.markers];
    state.lineStrings = [...keep.lineStrings];

    const positionsAfter = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));

    state.undoManager.push(new MergeRoutesCommand({
      keepRouteKey: currentRouteId,
      removedRouteKey: nextRouteId,
      keepRouteMarkersBefore: keepMarkersBefore,
      keepRouteLineStringsBefore: keepLineStringsBefore,
      keepRouteMarkersAfter: JSON.parse(JSON.stringify(keep.markers)),
      keepRouteLineStringsAfter: JSON.parse(JSON.stringify(keep.lineStrings)),
      removedRouteSnapshot: removedSnapshot,
      routePositionsBefore: positionsBefore,
      routePositionsAfter: positionsAfter,
      activeRouteKeyBefore: activeKeyBefore,
    }));

    await dispatch('addAllRoutesToMap');
  },

  async mergeAllRoutes({ state, getters, commit, dispatch }: PlanTrailActionContext) {
    const ordered = getters['routesArray'] as any[];
    const isRouteStyle = (r: any) => !r.route_style || r.route_style === 'route';
    const routeStyleRoutes = ordered.filter(isRouteStyle);
    if (routeStyleRoutes.length < 2) return;

    const keepRouteId = routeStyleRoutes[0].id as string;
    const toMergeIds = routeStyleRoutes.slice(1).map(r => r.id as string);

    const keep = state.routes[keepRouteId];
    if (!keep) return;

    // Capture state before merge
    const keepMarkersBefore = JSON.parse(JSON.stringify(keep.markers));
    const keepLineStringsBefore = JSON.parse(JSON.stringify(keep.lineStrings));
    const removedRouteSnapshots = toMergeIds.map(id => {
      const snap = JSON.parse(JSON.stringify(state.routes[id]));
      delete snap.undoMarkers; delete snap.undoLineStrings;
      delete snap.redoMarkers; delete snap.redoLineStrings;
      return { key: id, snapshot: snap };
    });
    const positionsBefore = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));
    const daysBefore = Object.keys(state.routes).map(k => ({ key: k, day: state.routes[k].day }));
    const activeKeyBefore = state.activeRouteKey!;

    // Merge markers and lineStrings from each route in position order, preserving existing geometry
    let newMarkers = [...keep.markers];
    let newLineStrings = [...keep.lineStrings];
    for (const id of toMergeIds) {
      const append = state.routes[id];
      if (!append || append.markers.length === 0) continue;
      const merged = await mergeRoutePair(newMarkers, newLineStrings, append.markers, append.lineStrings, dispatch);
      newMarkers = merged.markers;
      newLineStrings = merged.lineStrings;
    }

    keep.markers = newMarkers;
    keep.lineStrings = newLineStrings;
    keep.updated_at = new Date();

    // Remove the merged-in routes
    for (const id of toMergeIds) {
      commit('removeRoute', id);
    }

    const remainingOrder = (getters['routesArray'] as any[]).map(r => r.id);
    commit('updateRoutePositions', remainingOrder);
    commit('setActiveRoute', keepRouteId);

    // Collapse everything (route + alt/spur/transport) into a single day
    const targetDay = keep.day || 1;
    for (const key of Object.keys(state.routes)) {
      state.routes[key].day = targetDay;
    }

    // Sync legacy state since active route is now the merged one
    state.markers = [...keep.markers];
    state.lineStrings = [...keep.lineStrings];

    const positionsAfter = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));

    state.undoManager.push(new MergeAllRoutesCommand({
      keepRouteKey: keepRouteId,
      removedRouteSnapshots,
      keepRouteMarkersBefore: keepMarkersBefore,
      keepRouteLineStringsBefore: keepLineStringsBefore,
      keepRouteMarkersAfter: JSON.parse(JSON.stringify(keep.markers)),
      keepRouteLineStringsAfter: JSON.parse(JSON.stringify(keep.lineStrings)),
      routePositionsBefore: positionsBefore,
      routePositionsAfter: positionsAfter,
      routeDaysBefore: daysBefore,
      targetDay,
      activeRouteKeyBefore: activeKeyBefore,
    }));

    await dispatch('addAllRoutesToMap');
  },

  async splitRouteAtMarker({ state, getters, commit, dispatch }: PlanTrailActionContext, payload: { routeId: string, markerIndex: number }) {
    const { routeId, markerIndex } = payload;

    const route = state.routes[routeId];
    if (!route) {
      console.warn('Route not found for split:', routeId);
      return;
    }

    if (markerIndex <= 0 || markerIndex >= route.markers.length - 1) {
      return;
    }

    // Capture original state BEFORE split
    const originalMarkers = JSON.parse(JSON.stringify(route.markers));
    const originalLineStrings = JSON.parse(JSON.stringify(route.lineStrings));
    const positionsBefore = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));

    // Build marker arrays; include split marker in both halves
    const firstPartMarkers = JSON.parse(JSON.stringify(route.markers.slice(0, markerIndex + 1)));
    const secondPartMarkers = JSON.parse(JSON.stringify(route.markers.slice(markerIndex)));
    const firstPartLineStrings = JSON.parse(JSON.stringify(route.lineStrings.slice(0, markerIndex)));
    const secondPartLineStrings = JSON.parse(JSON.stringify(route.lineStrings.slice(markerIndex)));

    route.markers = firstPartMarkers;
    route.lineStrings = firstPartLineStrings;
    route.updated_at = new Date();

    const currentDay = route.day || 1;
    const newDay = currentDay + 1;

    // The second half becomes a day of its own: move everything after it
    // along a day first, so it doesn't land on top of the existing next day.
    const dayStateBefore = captureDayKeyedState(state);
    const dayStateAfter = splitIntoDays(dayStateBefore, state.routes, routeId, currentDay);
    applyDayKeyedState(state, dayStateAfter);

    const newRouteName = t('plan_trail.route_operations.part_two_name', { name: route.name });
    const newRoute = RouteFactory.createNew(newRouteName, route.position + 1, newDay);
    newRoute.markers = secondPartMarkers;
    newRoute.lineStrings = secondPartLineStrings;
    newRoute.route_style = route.route_style;

    commit('addRoute', newRoute);

    const currentOrder = (getters['routesArray'] as any[]).map(r => r.id).filter((id: string) => id !== newRoute.id);
    const reordered: string[] = [];
    for (const id of currentOrder) {
      reordered.push(id);
      if (id === routeId) {
        reordered.push(newRoute.id);
      }
    }
    commit('updateRoutePositions', reordered);
    commit('setActiveRoute', newRoute.id);

    const positionsAfter = Object.keys(state.routes).map(k => ({ key: k, position: state.routes[k].position }));

    // Push undo command
    const newRouteSnapshot = JSON.parse(JSON.stringify(state.routes[newRoute.id]));
    delete newRouteSnapshot.undoMarkers;
    delete newRouteSnapshot.undoLineStrings;
    delete newRouteSnapshot.redoMarkers;
    delete newRouteSnapshot.redoLineStrings;

    state.undoManager.push(new SplitRouteCommand({
      routeKey: routeId,
      originalMarkers,
      originalLineStrings,
      truncatedMarkers: JSON.parse(JSON.stringify(route.markers)),
      truncatedLineStrings: JSON.parse(JSON.stringify(route.lineStrings)),
      newRouteKey: newRoute.id,
      newRouteSnapshot,
      routePositionsBefore: positionsBefore,
      routePositionsAfter: positionsAfter,
      activeRouteKeyAfter: newRoute.id,
      dayStateBefore,
      dayStateAfter,
    }));

    await dispatch('addAllRoutesToMap');
  },
  async loopRoute({ state, commit, getters, dispatch }: PlanTrailActionContext) {
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;
    if (!activeRoute || activeRoute.markers.length === 0) {
      console.warn('No active route or markers to loop');
      return;
    }

    const startMarkerGeometry: MarkerGeometry = plainToClass(MarkerGeometry, activeRoute.markers[0].markerGeometry);

    // Create the loop marker near the start point
    const id = generateID();
    const marker = new MarkerFeature(
      new MarkerGeometry([startMarkerGeometry.lon() + 0.00001, startMarkerGeometry.lat() - 0.00001]),
      new MarkerProperties(id, true, Date.now().toString(), 0)
    );
    activeRoute.markers.push(marker);

    // Create linestring connecting the previous last marker to the new loop marker
    const prevMarker = activeRoute.markers[activeRoute.markers.length - 2];
    const provisional = await dispatch("createPolylineGeoJson", {
      startMarker: prevMarker,
      endMarker: marker
    });
    activeRoute.lineStrings.push(provisional);

    // Push command to undo manager
    state.undoManager.push(new LoopRouteCommand({
      routeKey: state.activeRouteKey!,
      marker,
      lineString: provisional,
    }));

    activeRoute.updated_at = new Date();
    await dispatch('updateRouteAndMarkers');
  },

  async backtrackRoute({ state, dispatch }: PlanTrailActionContext) {
    const routeKey = state.activeRouteKey;
    const activeRoute = routeKey ? state.routes[routeKey] : null;
    if (!routeKey || !activeRoute || activeRoute.markers.length < 2) {
      console.warn('No active route or insufficient markers to backtrack');
      return;
    }

    const originalMarkers = [...activeRoute.markers];
    const originalLineStrings = [...activeRoute.lineStrings];
    const addedMarkers: MarkerFeature[] = [];
    let addedLineStrings: LineStringFeature[] = [];

    for (let i = originalMarkers.length - 1; i >= 1; i--) {
      addedLineStrings.push(plainToClass(LineStringFeature, originalLineStrings[i - 1]).reverse());
      const marker = originalMarkers[i - 1];
      const id = generateID();
      // Markers read out of Vuex state are plain objects (deserialized / restored from undo),
      // so markerGeometry has no .lon()/.lat() methods — read coordinates directly.
      const coords = marker.markerGeometry.coordinates;
      addedMarkers.push(new MarkerFeature(
        new MarkerGeometry([coords[0], coords[1]]),
        new MarkerProperties(id, marker.markerProperties.isSnap, Date.now().toString(), 0)
      ));
    }

    if (addedLineStrings.some(isWheeledSegment)) {
      const before = routeFingerprint(activeRoute);
      // Return segment k runs from the marker the leg had reached to the k-th added one.
      const markers = [...originalMarkers, ...addedMarkers];
      const from = originalMarkers.length - 1;
      addedLineStrings = await Promise.all(addedLineStrings.map((line, k) =>
        isWheeledSegment(line) ? rerouted(line, markers[from + k], markers[from + k + 1]) : line
      ));
      // Edited while the routing was in flight: applying the return leg would throw that edit away.
      if (state.routes[routeKey] !== activeRoute || routeFingerprint(activeRoute) !== before) return;
    }

    activeRoute.lineStrings.push(...addedLineStrings);
    activeRoute.markers.push(...addedMarkers);
    state.undoManager.push(new BacktrackRouteCommand({
      routeKey,
      originalMarkerCount: originalMarkers.length,
      originalLineStringCount: originalLineStrings.length,
      addedMarkers,
      addedLineStrings,
    }));

    activeRoute.updated_at = new Date();
    await dispatch("updateRouteAndMarkers");
  },

  async clearMap({ state, dispatch, commit }: PlanTrailActionContext, skipConfirmation: boolean = false) {
    console.log('[CLEAR_MAP] clearMap called');
    if (!skipConfirmation) {
      const confirmed = confirm(t('plan_trail.route_operations.clear_map_confirm'));
      if (!confirmed) {
        console.log('[CLEAR_MAP] User cancelled');
        return;
      }
    }

    const map = getMap();
    const layerRegistry = getLayerRegistry();
    const sourceId = 'tracer-route';
    const layerId = 'tracer-route';
    const pointsSourceId = 'tracer-route-points';
    const pointsLayerId = 'tracer-route-points';

    // Remove tracer route if it exists
    // Always try to remove the layer first (it might exist even without source)
    if (map) {
      layerRegistry.removeLayer(layerId);
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }

      // Remove tracer route points (start and end markers)
      if (map.getLayer(pointsLayerId)) {
        map.removeLayer(pointsLayerId);
      }
      if (map.getSource(pointsSourceId)) {
        map.removeSource(pointsSourceId);
      }
    }

    // Clear the stored tracer route GeoJSON
    state.tracerRouteGeoJson = null;

    console.log('[CLEAR_MAP] Clearing all routes');
    // Clear all existing routes
    state.routes = {};

    // Create a new empty route. The state.routes map is keyed by route.id elsewhere
    // (e.g. addRoute, removeRoute) — using a different key here would orphan the route
    // so it can't be deleted via the route id.
    const newRoute = RouteFactory.createNew(t('plan_trail.initialization.route_number', { number: 1 }));
    state.routes[newRoute.id] = newRoute;
    state.activeRouteKey = newRoute.id;

    // Reset route counter
    state.routeCounter = 1;

    // Clear any other state that needs resetting
    state.selectedMarker = null;
    state.selectedLineString = null;
    state.showModal = '';
    state.miniCoordsBar = '';
    state.mapHoverDistance = null;

    // Disable removal mode if active
    if (state.removalMode) {
      await commit('disableRemovalMode');
    }

    console.log('[CLEAR_MAP] Calling addAllRoutesToMap to re-render');
    // Re-render the map with empty state
    await dispatch('addAllRoutesToMap');
    console.log('[CLEAR_MAP] clearMap complete');
  },

  async reverseFullRoute({ state, commit, dispatch }: PlanTrailActionContext) {
    const routeKey = state.activeRouteKey;
    const activeRoute = routeKey ? state.routes[routeKey] : null;

    // No day/route selected → reverse the ENTIRE itinerary: every route's
    // path, the order of routes, and the day labels are all flipped so the
    // whole hike reads back-to-front.
    if (!routeKey || !activeRoute) {
      if (Object.keys(state.routes).length === 0) {
        console.warn('No routes to reverse');
        return;
      }

      // Self-inverse — no data to capture
      state.undoManager.push(new ReverseItineraryCommand());
      // Reverse geometry AND re-key stage destinations to their new stage ends
      // BEFORE re-rendering, so the render-time prune keeps the attachments.
      reverseItineraryStateInPlace(state);

      // updateRouteAndMarkers re-renders every route (consolidated), which is
      // what we need here since there's no single active route to focus.
      await dispatch('updateRouteAndMarkers');
      return;
    }

    const reversed = { markers: [...activeRoute.markers], lineStrings: [...activeRoute.lineStrings] };
    reverseRouteInPlace(reversed);

    if (reversed.lineStrings.some(isWheeledSegment)) {
      const before = routeFingerprint(activeRoute);
      reversed.lineStrings = await Promise.all(reversed.lineStrings.map((line, index) =>
        isWheeledSegment(line)
          ? rerouted(line, reversed.markers[index], reversed.markers[index + 1])
          : line
      ));
      // Edited while the routing was in flight: applying the reverse would throw that edit away.
      if (state.routes[routeKey] !== activeRoute || routeFingerprint(activeRoute) !== before) return;
    }

    state.undoManager.push(new ReverseRouteCommand(routeKey, activeRoute, reversed));
    activeRoute.markers = reversed.markers;
    activeRoute.lineStrings = reversed.lineStrings;
    activeRoute.updated_at = new Date();

    // Re-key any stage/trail-start destination the route carried onto its
    // new anchor BEFORE re-rendering, so the render-time prune keeps the
    // attachment instead of dropping it.
    remapStageDestinations(state);
    await dispatch('updateRouteAndMarkers');
  },

  async updateElevation({ state, dispatch }: PlanTrailActionContext) {
    // Get the active route
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;
    if (!activeRoute) {
      console.warn('No active route to update elevation');
      return;
    }
    
    // Update linestrings with elevation data in the active route
    activeRoute.lineStrings = await Promise.all(activeRoute.lineStrings.map(async lineString => {
      const coords = lineString.lineStringGeometry.coordinates;
      const coordsWithUpdatedElevation = await getElevationData(coords.map(c => [c[1], c[0]]));
      return LineStringFeature.coordsOnlyFeature(coordsWithUpdatedElevation.map(h => [h.lng, h.lat, h.elevation]));
    }));
    
    // Update timestamp
    activeRoute.updated_at = new Date();

    // Only update route polylines — elevation changes don't affect markers
    await dispatch('updateRouteSources');
  }
};
